import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth.jsx";
import { LOGO_COLOR } from "@/lib/brand";
import { Loader2, User, Mail, Lock } from "lucide-react";

export default function Signup() {
  const { signup } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const user = await signup(form.name, form.email, form.password);
      toast.success(`Welcome ${user.name.split(" ")[0]}! You're on the Free plan.`);
      navigate("/app");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Sign up failed");
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen bg-mesh bg-brand-bg flex items-center justify-center p-5">
      <div className="w-full max-w-md">
        <Link to="/" className="flex items-center gap-2 justify-center mb-8">
          <img src={LOGO_COLOR} alt="DraftVoice" className="h-10 w-10 rounded-lg bg-brand-navy" />
          <span className="font-bold text-xl text-brand-navy">Draft<span className="text-gradient-primary">Voice</span> AI</span>
        </Link>
        <div className="bg-white rounded-3xl shadow-soft border border-brand-borderc p-8">
          <h1 className="text-2xl font-bold text-brand-navy">Create your account</h1>
          <p className="text-sm text-brand-muted mt-1">2,000 words / week — free forever.</p>
          <form onSubmit={submit} className="mt-6 space-y-4">
            <div>
              <label className="text-sm font-medium text-brand-text">Name</label>
              <div className="relative mt-1.5">
                <User className="absolute left-3 top-3 w-4 h-4 text-brand-muted" />
                <input
                  data-testid="signup-name-input"
                  required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                  className="w-full pl-10 pr-3 py-2.5 rounded-xl border border-brand-borderc bg-white focus:border-brand-purple focus:ring-2 focus:ring-brand-purple/20 outline-none"
                  placeholder="Your full name"
                />
              </div>
            </div>
            <div>
              <label className="text-sm font-medium text-brand-text">Email</label>
              <div className="relative mt-1.5">
                <Mail className="absolute left-3 top-3 w-4 h-4 text-brand-muted" />
                <input
                  data-testid="signup-email-input"
                  type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
                  className="w-full pl-10 pr-3 py-2.5 rounded-xl border border-brand-borderc bg-white focus:border-brand-purple focus:ring-2 focus:ring-brand-purple/20 outline-none"
                  placeholder="you@company.com"
                />
              </div>
            </div>
            <div>
              <label className="text-sm font-medium text-brand-text">Password</label>
              <div className="relative mt-1.5">
                <Lock className="absolute left-3 top-3 w-4 h-4 text-brand-muted" />
                <input
                  data-testid="signup-password-input"
                  type="password" required minLength={6} value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })}
                  className="w-full pl-10 pr-3 py-2.5 rounded-xl border border-brand-borderc bg-white focus:border-brand-purple focus:ring-2 focus:ring-brand-purple/20 outline-none"
                  placeholder="At least 6 characters"
                />
              </div>
            </div>
            <button
              data-testid="signup-submit-btn"
              type="submit" disabled={loading}
              className="w-full btn-primary-gradient rounded-full py-3 font-semibold flex items-center justify-center gap-2 disabled:opacity-60"
            >
              {loading && <Loader2 className="w-4 h-4 animate-spin" />}
              Create account
            </button>
          </form>
          <div className="mt-5 text-center text-sm text-brand-muted">
            Already have an account? <Link to="/login" className="text-brand-blue font-semibold hover:underline">Log in</Link>
          </div>
        </div>
      </div>
    </div>
  );
}
